/**
 * The Errors tab's words (19/10/2026): a kind's label, hint and tone from
 * the shared table (an unknown kind reads neutrally), the badge tone of a
 * tone, where an error happened in one line, and the Slack state in words.
 * For Media Devoted | Created By Ciro Autiero
 */
import { DROP_GEN_ERROR_WORDS, type DropGenErrorKind, type DropGenErrorRow, type DropGenErrorTone, type DropGenErrorsSlackState } from '@trafficpulse/shared';

export const kindWords = (kind: string): { label: string; hint: string; tone: DropGenErrorTone; fix: string } =>
  DROP_GEN_ERROR_WORDS[kind as DropGenErrorKind] ?? { label: kind.replace(/_/g, ' '), hint: 'An error this build has no words for yet.', tone: 'error', fix: 'Send this message to the developers.' };

/** Red only when sends stop or a person must act; amber for a step that failed; grey for a read that failed. */
export const BADGE_TONE: Record<DropGenErrorTone, 'destructive' | 'warning' | 'secondary'> = { critical: 'destructive', error: 'warning', warning: 'secondary' };

/** The chart's and tiles' colour per tone — one hue each, status colours never reused for series. */
export const TONE_COLOR: Record<DropGenErrorTone, string> = {
  critical: 'hsl(var(--destructive))',
  error: 'hsl(var(--warning))',
  warning: 'hsl(var(--muted-foreground))',
};

export const TONE_LABEL: Record<DropGenErrorTone, string> = { critical: 'Critical', error: 'Error', warning: 'Read failed' };

/** Kinds that are read failures — the automation judged with less, nothing stopped. */
export const isRead = (kind: string) => kindWords(kind).tone === 'warning';
export const isCritical = (kind: string) => kindWords(kind).tone === 'critical';

const shortRoute = (name: string) => name.replace(/^\([^)]*\)\s*/, '').replace(/\s*\([^)]*\)\s*$/, '').trim();

/** "WARM UP · 23Telecom · AU_CLICKERS" — the pieces the row knows, in that order; "system-wide" for a "*" row. */
export const whereText = (row: DropGenErrorRow): string =>
  [row.containerName, row.routeName ? shortRoute(row.routeName) : row.routeId !== null ? `gateway #${row.routeId}` : null, row.listName]
    .filter(Boolean)
    .join(' · ') || (row.countryKey === '*' ? 'system-wide' : 'the country');

/** The Slack chip: on with the last delivery, or why not. */
export const slackWords = (s: DropGenErrorsSlackState): { label: string; tone: 'success' | 'warning' | 'destructive'; hint: string } => {
  if (!s.on) return { label: 'Slack off', tone: 'warning', hint: `${s.why ?? 'Slack is not set up.'} Until then errors are traced here only.` };
  if (s.lastError) return { label: 'Slack failing', tone: 'destructive', hint: `The last delivery failed: ${s.lastError}. Errors are traced here; the channel is tried again at the next alert.` };
  return { label: 'Every error → Slack', tone: 'success', hint: "Every error goes to the country's Slack thread as a ⛔ line (the Errors topic is on)." };
};

/** A resolution in two or three words for the narrow Status column ("texts filled", "a new day", "sent"); the full words sit in its tooltip and the detail. */
export const shortResolution = (text: string | null): string => {
  if (!text) return 'resolved';
  const cut = text.split(/ on | for | — | · |, | \(/)[0].trim();
  return cut.length > 26 ? `${cut.slice(0, 24).trimEnd()}…` : cut;
};
